import React, { useState, useMemo } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';

import { Grid, Snackbar, SnackbarContent, Typography, TextField, Hidden, Button, makeStyles, Box } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';

import { AuthenticationService } from '../../services/AuthenticationService';

import { TelemetryService } from '../../../src//services/TelemetryService';
import { EventTracking, Location as PageLocation } from '../../../src//utilities/Tracking/EventTracking';
import { ProfitWell } from '../../utilities/Tracking/ProfitWell';
import { FieldValidationError, ValidationHelper } from '../../services/server/ServerResponses';
import { PageTitle } from '../../components/PageTitle';
import { useUser } from '../../utilities/useUser';
import { routes } from '../../routes';
import { useQueryParams } from '../../components/useQueryParams';
import { AuthLayout } from './AuthLayout';
import { GlooButton } from '../../components/GlooButton';
import AppConfig, { AppConfigEnvironment } from '../../entities/AppConfig';
import { MakerSubscriptionStatus } from '../../entities/MakerSubscription';

const useStyles = makeStyles((theme) => ({
    title: {
        fontSize: "1.7rem",
        fontWeight: 500
    },
    textButton: {
        paddingLeft: '0px',
        paddingRight: '0px'
    },
    submitButton: {
        minWidth: '150px',
        minHeight: '50px',
    },
    submitButtonMobile: {
        marginTop: '20px',
        minHeight: '50px',
    },
    buttonContainer: {
        padding: '30px 0px 50px 0px'
    },
    link: {
        textDecoration: 'none'
    },
    messageLink: {
        color: "#00a5e7"
    },
    snackbarContent: {
        backgroundColor: theme.palette.error.dark,
    },
    snackbarContentIcon: {
        fontSize: 20,
        opacity: 0.9,
        marginRight: theme.spacing(1),
    },
    snackbarMessage: {
        display: 'flex',
        alignItems: 'center',
    },
    termsText: {
        paddingBottom: 55
    },
}));

interface Props {
    config: AppConfig;
}

export function SignIn(props: Props){
    const query = useQueryParams();
    const history = useHistory()
    
    const classes = useStyles();
    const userContext = useUser();
    const user = userContext.onlineUser;

    const [disabled, setDisabled] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
	const redirectUrl = useMemo(() => query.get("redirect") ?? routes.app.libraryHomePage, [query]);

    const glooRedirectUrl = props.config.makerSiteUrl + routes.public.signInPage + '?redirect=' + redirectUrl;
    const glooUrl = props.config.mainSiteUrl + '/account/gloo/login?redirectUri=' + encodeURIComponent(glooRedirectUrl);

    const handleFormSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (disabled)
            return;

        if(email.trim().length === 0)
            return setErrors([{field: "Email", errors: ["Invalid Email Address"]}]);

        setDisabled(true);
        const response = await AuthenticationService.signIn(email, password, false);
        setDisabled(false);

        if(response.error) return setErrors([{ field: "", errors: ['An unknown error occurred. Please try again.'] }]);
        if(response.validation) return setErrors(response.errors);

        ProfitWell.onUserAuthenticated(response.data.email);
        TelemetryService.updateContactField("sc_cloud_subscription", response.data.makerSubscription?.status === MakerSubscriptionStatus.Active ? "Paid" : "Free");
        TelemetryService.recordUserActivity();
        EventTracking.Authentication.onLogin(PageLocation.Library);
        
        userContext.updateUser(response.data);

    }

    if (user)
        return <Redirect to={redirectUrl} />;

    return (
        <AuthLayout>
            <PageTitle title="Sermon Maker - Sign In" />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={ValidationHelper.hasGenericError(errors)}
            >
                <SnackbarContent
                    className={classes.snackbarContent}
                    aria-describedby="client-snackbar"
                    message={
                        <span id="client-snackbar" className={classes.snackbarMessage}>
                            <ErrorIcon className={classes.snackbarContentIcon} />
                            <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getGenericErrorSummary(errors) }} />
                        </span>}
                />
            </Snackbar>
            <Grid container direction="column" justifyContent="center" alignItems="center">
                <Typography variant="h5" style={{fontSize: "1.7rem", fontWeight: 500}}>Sign in</Typography>
            </Grid>
            <form onSubmit={e => handleFormSubmit(e)}>
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Email')}
                    required
                    autoFocus
                    fullWidth
                    id="email"
                    label="Email"
                    type="email"
                    margin="dense"
                    onChange={e => setEmail(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Email') }} />}
                />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Password')}
                    required
                    fullWidth
                    id="password"
                    label="Password"
                    type="password"
                    margin="dense"
                    onChange={e => setPassword(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Password') }} />}
                />
                <Hidden smDown>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Link to={routes.public.resolve.reqisterPage(history.location.search)} className={classes.link}>
                            <Button variant="text" size="small" color="primary" type="button" className={classes.textButton}>Create Account</Button>
                        </Link>
                        <Link to={routes.public.forgotPasswordPage} className={classes.link}>
                            <Button variant="text" size="small" color="primary" type="button" className={classes.textButton}>Forgot Password?</Button>
                        </Link>
                    </Grid>
                    <Grid container direction="row" justifyContent="space-around" alignItems="center" className={classes.buttonContainer}>
                        <Button variant="contained" size="large" color="primary" type="submit" disabled={disabled} className={classes.submitButton}>Sign In</Button>
                        <GlooButton url={glooUrl} disabled={disabled} style={{minWidth: 220, marginLeft: 10}} />
                    </Grid>
                </Hidden>
                <Hidden mdUp>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Link to={routes.public.resolve.reqisterPage(history.location.search)} className={classes.link}>
                            <Button variant="text" size="small" color="primary" type="button" className={classes.textButton}>Create Account</Button>
                        </Link>
                        <Link to={routes.public.forgotPasswordPage} className={classes.link}>
                            <Button variant="text" size="small" color="primary" type="button" className={classes.textButton}>Forgot Password?</Button>
                        </Link>
                    </Grid>
                    <Box marginBottom={5}>
                        <Button fullWidth variant="contained" size="large" color="primary" type="submit" disabled={disabled} className={classes.submitButtonMobile}>Sign In</Button>
                        <GlooButton url={glooUrl} disabled={disabled} fullWidth style={{marginTop: 20}} />
                    </Box>
                </Hidden>
                <Typography variant="body1" className={classes.termsText}>*Sign in to your Sermon Maker account using your SermonCentral.com account details.
                    Or, <Link to={routes.public.resolve.reqisterPage(history.location.search)} className={classes.messageLink}>create a new account</Link> to get started today.</Typography>
            </form>
        </AuthLayout>
    );
}